// Seitentitel-Kanal zwischen Seite und Shell (DESIGN §3.5.1, §1.5): Die People- und Admin-Topbar
// zeigen den Titel der Seite als h1; die Seite meldet ihn über `PageHeader` bzw. `usePageTitle`.
// Shells ohne großen Topbar-Titel (Bar AI) setzen `titleInTopbar={false}`; dann rendert der
// PageHeader den Titel im Inhalt.
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';

export interface BackTarget {
  href: string;
  label: string;
}

export interface PageTitleEntry {
  title: string;
  back?: BackTarget;
}

interface PageChromeValue {
  titleInTopbar: boolean;
  current: (PageTitleEntry & { owner: string }) | null;
  publish: (owner: string, entry: PageTitleEntry | null) => void;
}

const PageChromeContext = createContext<PageChromeValue | null>(null);

export function PageChromeProvider({
  children,
  titleInTopbar = true,
}: {
  children: ReactNode;
  titleInTopbar?: boolean;
}) {
  const [current, setCurrent] = useState<PageChromeValue['current']>(null);
  const publish = useCallback((owner: string, entry: PageTitleEntry | null) => {
    setCurrent((prev) => {
      if (entry) {
        if (
          prev &&
          prev.owner === owner &&
          prev.title === entry.title &&
          prev.back?.href === entry.back?.href
        )
          return prev;
        return { ...entry, owner };
      }
      return prev?.owner === owner ? null : prev;
    });
  }, []);
  const value = useMemo(
    () => ({ titleInTopbar, current, publish }),
    [titleInTopbar, current, publish],
  );
  return <PageChromeContext.Provider value={value}>{children}</PageChromeContext.Provider>;
}

/** Shell-Seite: aktueller Titel und Zurück-Ziel (null = Shell-Standard). */
export function usePageChrome(): { titleInTopbar: boolean; title: PageTitleEntry | null } {
  const value = useContext(PageChromeContext);
  return { titleInTopbar: value?.titleInTopbar ?? false, title: value?.current ?? null };
}

/**
 * Meldet den Seitentitel an die Topbar und setzt `document.title`.
 * Gibt zurück, ob die Seite den Titel selbst rendern muss (kein Topbar-Titel).
 */
export function usePageTitle(
  title: string | undefined,
  back?: BackTarget,
  appName = 'Bar',
): boolean {
  const value = useContext(PageChromeContext);
  const owner = useId();
  const publish = value?.publish;
  const backHref = back?.href;
  const backLabel = back?.label;
  useEffect(() => {
    if (!title) return;
    document.title = `${title} · ${appName}`;
    if (!publish) return;
    publish(owner, {
      title,
      back: backHref ? { href: backHref, label: backLabel ?? 'Back' } : undefined,
    });
    return () => publish(owner, null);
  }, [publish, owner, title, backHref, backLabel, appName]);
  return !value?.titleInTopbar;
}
